import { QueryClient } from "@tanstack/react-query";

// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors except 408, 429
        if (error instanceof Error) {
          const status = (error as any).response?.status;
          if (
            status >= 400 &&
            status < 500 &&
            status !== 408 &&
            status !== 429
          ) {
            return false;
          }
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry mutations on 4xx errors
        if (error instanceof Error) {
          const status = (error as any).response?.status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth queries
  auth: {
    me: () => ["auth", "me"] as const,
    verify: () => ["auth", "verify"] as const,
  },

  // User queries
  users: {
    all: () => ["users"] as const,
    list: (filters?: any) => ["users", "list", filters] as const,
    detail: (id: string) => ["users", "detail", id] as const,
    profile: (id: string) => ["users", "profile", id] as const,
  },

  // Project queries
  projects: {
    all: () => ["projects"] as const,
    list: (filters?: any) => ["projects", "list", filters] as const,
    detail: (id: string) => ["projects", "detail", id] as const,
    components: (projectId: string) =>
      ["projects", projectId, "components"] as const,
    members: (projectId: string) => ["projects", projectId, "members"] as const,
    versions: (projectId: string) =>
      ["projects", projectId, "versions"] as const,
    collaboration: (projectId: string) =>
      ["projects", projectId, "collaboration"] as const,
  },

  // Component queries
  components: {
    all: () => ["components"] as const,
    list: (projectId: string, filters?: any) =>
      ["components", "list", projectId, filters] as const,
    detail: (id: string) => ["components", "detail", id] as const,
    hierarchy: (projectId: string) =>
      ["components", "hierarchy", projectId] as const,
    search: (query: string, projectId?: string) =>
      ["components", "search", query, projectId] as const,
  },

  // AI queries
  ai: {
    generate: () => ["ai", "generate"] as const,
    modify: () => ["ai", "modify"] as const,
    suggestions: (context: any) => ["ai", "suggestions", context] as const,
    history: () => ["ai", "history"] as const,
  },

  // Collaboration queries
  collaboration: {
    session: (projectId: string) =>
      ["collaboration", "session", projectId] as const,
    participants: (sessionId: string) =>
      ["collaboration", "participants", sessionId] as const,
    changes: (projectId: string) =>
      ["collaboration", "changes", projectId] as const,
  },

  // Assets queries
  assets: {
    all: () => ["assets"] as const,
    list: (projectId?: string) => ["assets", "list", projectId] as const,
    detail: (id: string) => ["assets", "detail", id] as const,
  },
} as const;

// Default query options for different types of data
export const queryOptions = {
  // Fast-changing data (real-time features)
  realTime: {
    staleTime: 0,
    gcTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // 30 seconds
  },

  // Slow-changing data (user profiles, project metadata)
  stable: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  // Static data (rarely changes)
  static: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },

  // Background refresh for important data
  background: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchIntervalInBackground: true,
  },
} as const;
